import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import Disclaimer from "@/components/Disclaimer";
import InstallAppButton from "@/components/InstallAppButton";
import DesktopShortcutButton from "@/components/DesktopShortcutButton";
import { Stethoscope, Activity, FlaskConical, ClipboardCheck, BookMarked, ArrowRight } from "lucide-react";

const stages = [
  { icon: Stethoscope, title: "Symptom Intake", text: "Patient enters complaint; the engine asks focused follow-up questions." },
  { icon: Activity, title: "Clinical Examination", text: "System prompts the doctor for positive & negative signs to elicit." },
  { icon: ClipboardCheck, title: "Differential Diagnosis", text: "A ranked, evidence-weighted differential set is reached." },
  { icon: FlaskConical, title: "Investigations", text: "Appropriate tests are ordered to separate the differentials." },
  { icon: BookMarked, title: "Final Diagnosis & Rx", text: "Evidence-based diagnosis and treatment, cited to source." },
];

export default function Landing() {
  const { user, checked } = useAuth();
  if (checked && user) return <Navigate to="/workflow" replace />;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Disclaimer />
      <header className="border-b border-zinc-200 px-5 sm:px-10 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-md bg-[#002FA7] flex items-center justify-center">
            <Stethoscope className="h-4 w-4 text-white" />
          </div>
          <span className="font-serif text-xl tracking-tight text-zinc-900">Medical CDSS</span>
        </div>
        <div className="flex items-center gap-3">
          <InstallAppButton />
          <DesktopShortcutButton />
          <Link to="/auth" data-testid="header-login-link">
            <Button variant="outline" className="border-zinc-300">Sign in</Button>
          </Link>
        </div>
      </header>

      <main className="flex-1 px-5 sm:px-10 lg:px-16 py-14 lg:py-20 max-w-6xl">
        <p className="text-sm font-semibold tracking-widest uppercase text-[#002FA7] mb-5">
          Clinical Decision Support System
        </p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-zinc-900 max-w-3xl leading-[1.05]">
          Reason from symptom to evidence-based diagnosis.
        </h1>
        <p className="mt-6 text-base sm:text-lg text-zinc-600 max-w-2xl leading-relaxed">
          A structured diagnostic workflow grounded in Harrison's Principles of Internal Medicine
          (22nd edition) and the New England Journal of Medicine, British Medical Journal &amp; JAMA.
          Patient-reported symptoms and doctor findings converge on a final diagnosis and treatment plan.
        </p>
        <div className="mt-9 flex flex-wrap gap-3">
          <Link to="/workflow" data-testid="get-started-btn">
            <Button className="bg-[#002FA7] hover:bg-[#002687] text-white h-11 px-7 text-base">
              Begin a case <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
          <Link to="/auth" data-testid="landing-signin-link">
            <Button variant="outline" className="border-zinc-300 h-11 px-7 text-base">Sign in to save cases</Button>
          </Link>
        </div>
        <p className="mt-3 text-sm text-zinc-400">No sign-in required to use — create an account only if you want to save case history.</p>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-zinc-200 border border-zinc-200 rounded-md overflow-hidden">
          {stages.map((s, i) => (
            <div key={s.title} className="bg-white p-6">
              <div className="flex items-center gap-2 text-[#002FA7]">
                <s.icon className="h-5 w-5" />
                <span className="text-xs font-semibold text-zinc-400">0{i + 1}</span>
              </div>
              <h3 className="mt-4 font-serif text-lg text-zinc-900">{s.title}</h3>
              <p className="mt-2 text-sm text-zinc-600 leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
